import { useQuery } from "@tanstack/react-query";
import { Lightbulb, Telescope } from "lucide-react";
import { HandoffLink, useApi } from "@ncp/api-client";
import { formatDate, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, EmptyState, Skeleton } from "@ncp/ui";
import { backofficeHref } from "../../lib/links";

export interface ExPostLesson { caseId: string; caseNumber: string; caseTitle: string; outcome: string; verdict: string; completedAt: string; policyLessons?: string | null; policyLessonsAr?: string | null; markets: string[] }
export interface ExPostListItem { caseId: string; caseNumber: string; caseTitle: string; outcome: string; status: string; dueAt: string; overdue: boolean; readAt?: string | null; verdict?: string | null; completedAt?: string | null }

const VERDICT_VARIANT: Record<string, "success" | "warning" | "danger" | "outline"> = { AsPredicted: "success", BetterThanPredicted: "success", WorseThanPredicted: "danger", Inconclusive: "outline" };

/**
 * What the Department's decisions taught it: the lessons recorded by completed ex-post evaluations, next to the evaluations still due – the
 * evidence the analysis policy review reads before it moves a threshold, a margin or an entry assumption.
 */
export function ExPostLessonsPanel() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const lessons = useQuery({ queryKey: ["ec-ex-post-lessons"], queryFn: () => api.get<ExPostLesson[]>("/api/ec/ex-post/lessons") });
  const list = useQuery({ queryKey: ["ec-ex-post-list"], queryFn: () => api.get<ExPostListItem[]>("/api/ec/ex-post") });
  const ar = locale === "ar";
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <Card>
        <CardHeader className="space-y-1.5 p-6 pb-4">
          <CardTitle className="flex items-center gap-2 text-lg"><Lightbulb className="size-4 text-aegold-700" /> {t("admin:analysisPolicy.lessons")}</CardTitle>
          <CardDescription>{t("admin:analysisPolicy.lessonsHint")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 p-6 pt-0">
          {lessons.isLoading ? <Skeleton className="h-32" /> : !lessons.data?.length ? <EmptyState icon={Lightbulb} title={t("admin:analysisPolicy.noLessons")} description={t("admin:analysisPolicy.noLessonsHint")} className="py-8" /> : lessons.data.map((l) => (
            <div key={l.caseId} className="rounded-xl border border-sand-200 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <HandoffLink href={backofficeHref(`/cases/${l.caseId}?tab=analysis&view=expost`)} className="font-medium text-aegold-800 hover:underline">{l.caseNumber}</HandoffLink>
                <span className="truncate text-muted-foreground">{l.caseTitle}</span>
                <Badge size="sm" variant={VERDICT_VARIANT[l.verdict] ?? "outline"}>{t(`admin:workspace.exPost.verdicts.${l.verdict}`)}</Badge>
                <span className="ms-auto text-xs text-muted-foreground">{formatDate(l.completedAt, locale)}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed">{ar && l.policyLessonsAr ? l.policyLessonsAr : l.policyLessons}</p>
              {l.markets.length ? <p className="mt-1 text-[11px] text-muted-foreground">{l.markets.join(" · ")}</p> : null}
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="space-y-1.5 p-6 pb-4">
          <CardTitle className="flex items-center gap-2 text-lg"><Telescope className="size-4 text-aegold-700" /> {t("admin:analysisPolicy.evaluations")}</CardTitle>
          <CardDescription>{t("admin:analysisPolicy.evaluationsHint")}</CardDescription>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          {list.isLoading ? <Skeleton className="h-32" /> : !list.data?.length ? <EmptyState icon={Telescope} title={t("admin:analysisPolicy.noEvaluations")} className="py-8" /> : (
            <table className="w-full text-xs">
              <thead><tr className="text-sand-600"><th className="py-1.5 text-start font-medium">{t("misc.caseNumber")}</th><th className="py-1.5 text-start font-medium">{t("admin:workspace.exPost.decision")}</th><th className="py-1.5 text-start font-medium">{t("admin:workspace.exPost.dueAt")}</th><th className="py-1.5 text-start font-medium">{t("misc.status")}</th></tr></thead>
              <tbody>
                {list.data.map((e) => (
                  <tr key={e.caseId} className="border-t border-sand-200">
                    <td className="py-2 pe-2"><HandoffLink href={backofficeHref(`/cases/${e.caseId}?tab=analysis&view=expost`)} className="font-medium text-aegold-800 hover:underline">{e.caseNumber}</HandoffLink><p className="truncate text-muted-foreground">{e.caseTitle}</p></td>
                    <td className="py-2 pe-2">{e.outcome}</td>
                    <td className="py-2 pe-2">{formatDate(e.dueAt, locale)}</td>
                    <td className="py-2"><Badge size="sm" variant={e.status === "Completed" ? "success" : e.overdue ? "danger" : "outline"} dot>{t(`admin:workspace.exPost.status.${e.status}`)}</Badge>{e.verdict ? <span className="ms-1 text-muted-foreground">{t(`admin:workspace.exPost.verdicts.${e.verdict}`)}</span> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
